import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Send, X, DoorOpen } from "lucide-react";
import { API } from "@/lib/api";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function MavulaAssistant() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid());
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: t("assistant.greeting"),
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Auto-open after 10 seconds on the homepage (once per browser session)
  useEffect(() => {
    if (window.location.pathname !== "/") return;
    if (sessionStorage.getItem("mv_bot_autoopened") === "1") return;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem("mv_bot_autoopened", "1");
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: q, language: i18n.language }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[ERROR]")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: "Sorry, I could not reply right now. Please try WhatsApp. Ask me anything about MAVULA Investment — Open Doors!" };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              text: (copy[copy.length - 1].text || "") + line,
            };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "I could not reach the server. Please tap WhatsApp to talk to us live. Ask me anything about MAVULA Investment — Open Doors!" };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="mavula-assistant-panel"
          className="chat-panel fixed z-40 bottom-24 right-5 w-[92vw] max-w-[380px] mv-card overflow-hidden flex flex-col"
          style={{ height: "min(560px, 80vh)" }}
        >
          <div className="mv-green-bg text-white px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--mv-gold)" }}>
              <DoorOpen size={16} className="mv-green-text" />
            </div>
            <div className="flex-1">
              <div className="font-display text-[17px] leading-tight">{t("assistant.title")}</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-white/75">{t("assistant.sub")}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded-full" data-testid="close-assistant-btn">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[var(--mv-bg)]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-snug ${
                    m.role === "user"
                      ? "text-white"
                      : "bg-white border mv-hairline"
                  }`}
                  style={m.role === "user" ? { background: "var(--mv-green)" } : {}}
                >
                  {m.text || <span className="opacity-50">…</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t mv-hairline flex flex-col gap-2 bg-white">
            <a
              href={`https://wa.me/447424311882?text=${encodeURIComponent("Hi MAVULA, I'd like to speak to a human agent. " + (messages.filter((m) => m.role === "user").slice(-1)[0]?.text || ""))}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border mv-hairline text-[12.5px] hover:bg-black/[0.04]"
              style={{ color: "#0A7A2C" }}
              data-testid="assistant-handoff-whatsapp"
            >
              💬 Talk to a human on WhatsApp
            </a>
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("assistant.placeholder")}
                className="flex-1 px-4 py-2.5 rounded-full text-[14px] border mv-hairline bg-white"
                data-testid="assistant-input"
              />
              <button
                onClick={send}
                disabled={sending}
                className="btn-green px-4 py-2.5 disabled:opacity-60"
                data-testid="assistant-send-btn"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid="mavula-assistant-toggle"
        className="fixed z-40 bottom-5 right-5 flex items-center gap-2 rounded-full pl-3 pr-5 py-3 shadow-lg btn-green"
        aria-label="Open MAVULA Assistant"
      >
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--mv-gold)" }}>
          <Sparkles size={18} className="mv-green-text" strokeWidth={2} />
        </span>
        <span className="hidden sm:flex flex-col leading-tight text-left">
          <span className="font-semibold text-[14px]">{t("assistant.toggle_label")}</span>
          <span className="text-[11px] text-white/80">{t("assistant.toggle_sub")}</span>
        </span>
      </button>
    </>
  );
}
